import { LookupNoYesType, Permission, BloodCollectionForm as BloodCollectionFormDto } from "@/api/dtos";
import { faCircleInfo } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid2, styled, Tooltip, Typography, useTheme } from "@mui/material";
import { ConditionMode, ValidateOn } from "@ngt/forms-core";
import { Checkbox, CollaboratingGroupContext, CrfCondition, CrfForm, DatePicker, FormBreadcrumbs, GroupedField, ICrfConditionParameters, InstitutionContext, MasterGroupContext, PageLayout, PatientContext, RadioGroup, RouteLoading, Text, ToggleButtonGroup } from "@ngt/opms";
import { crfReadOnly } from "@/utils/CrfFormUtils";
import { usePermissionsByIds } from "@ngt/opms-bctapi";
import { FC, useCallback, useContext } from "react";

const StyledSubsectionHeading = styled(Typography) (({theme}) => ({
    padding: theme.spacing(2, 2),
    fontSize: theme.typography.h2.fontSize,
    color: theme.palette.secondary.light,
}))

interface IBloodCollectionFormProps {

}

const BloodCollectionForm: FC<IBloodCollectionFormProps> = () => {
    const theme = useTheme();

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const samplesShippedToCentralLab = useCallback((params: ICrfConditionParameters<BloodCollectionFormDto>) => 
        params.formState?.value?.samplesShippedToCentralLab == LookupNoYesType.Yes
    , []);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                enableQueries
            >
                <GroupedField
                    propertyPath="bloodCollected"
                    component={ToggleButtonGroup}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={(params) => params.formState?.value.bloodCollected == LookupNoYesType.Yes}
                >
                    <GroupedField 
                        propertyPath="collectionDate"
                        component={DatePicker}
                        format={"dd-MMM-yyyy"}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={(params) => params.formState?.value.bloodCollected == LookupNoYesType.No}
                >
                    <GroupedField
                        propertyPath="reasonForNonCollection"
                        component={Text}
                        multiline
                        rows={4}
                        LabelGridProps={{
                            sx: {
                                alignSelf: "flex-start",
                                mt: 0.5
                            }
                        }}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <StyledSubsectionHeading>
                    <Grid2
                        container
                        spacing={2}
                        alignItems={"center"}
                    >
                        What was collected?    
                        <Tooltip
                            title={"If unknown, leave blank"}
                        >
                            <FontAwesomeIcon 
                                icon={faCircleInfo} 
                                color={theme.palette.primary.light}
                                cursor={"pointer"}
                            />
                        </Tooltip>
                    </Grid2>
                </StyledSubsectionHeading>
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={(params) => params.formState?.value.bloodCollected == LookupNoYesType.Yes}
                >
                    <GroupedField
                        propertyPath="plasmaCollected"
                        component={Checkbox}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={(params) => params.formState?.value.bloodCollected == LookupNoYesType.Yes}
                >
                    <GroupedField
                        propertyPath="nlcCollected"
                        component={Checkbox}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={(params) => params.formState?.value.bloodCollected == LookupNoYesType.Yes}
                >
                    <GroupedField
                        propertyPath="pbmcCollected"
                        component={Checkbox}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <GroupedField
                    propertyPath="samplesShippedToCentralLab"
                    component={RadioGroup}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={samplesShippedToCentralLab}
                >
                    <GroupedField
                        propertyPath="dateSamplesShipped"
                        component={DatePicker}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
            </CrfForm>
        </PageLayout>
    )
}

export default BloodCollectionForm;